/**
 * AuditProtectionExtension — Layer 1 structural protection for audit artifacts.
 *
 * Once the document contains any CitationNode (the structural byproduct of a
 * completed audit), three user actions are blocked or transformed:
 *
 *   1. Citation deletion — transactions that would remove any CitationNode
 *      (by backspace, delete, select-all + type, etc.) are rejected.
 *   2. Paste with citations — pasted slices have their citation nodes stripped
 *      so clipboard round-trips can't produce duplicate claimIds.
 *   3. (Companion change in Workbench.tsx) Audit-visualization transactions
 *      set addToHistory:false so Cmd+Z cannot unwind them.
 *
 * Activation is derived from the document itself: if no CitationNodes exist,
 * all transactions pass through unchanged. This keeps protection correct even
 * when React state (apiAuditResult) is out of sync with the editor — e.g.
 * after an audit failure where the old citations remain in the doc.
 *
 * System-initiated citation mutations (e.g. visualizeApiCitations) must set
 * `tr.setMeta(auditProtectionPluginKey, { bypass: true })` to pass through.
 */

import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Fragment, Slice, type Node as PMNode } from '@tiptap/pm/model'
import { Extension } from '@tiptap/react'
import { toast } from 'sonner'

export const auditProtectionPluginKey = new PluginKey('auditProtection')

interface AuditProtectionStorage {
  hasShownBlockedToast: boolean
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function collectCitationIds(doc: PMNode): Set<string> {
  const ids = new Set<string>()
  doc.descendants((node) => {
    if (node.type.name === 'citation') {
      const id = node.attrs.claimId
      if (typeof id === 'string' && id.length > 0) ids.add(id)
    }
  })
  return ids
}

function stripCitationsFromFragment(frag: Fragment): Fragment {
  const out: PMNode[] = []
  frag.forEach((child) => {
    if (child.type.name === 'citation') return
    if (child.childCount > 0) {
      out.push(child.copy(stripCitationsFromFragment(child.content)))
    } else {
      out.push(child)
    }
  })
  return Fragment.fromArray(out)
}

function stripCitationsFromSlice(slice: Slice): Slice {
  return new Slice(
    stripCitationsFromFragment(slice.content),
    slice.openStart,
    slice.openEnd,
  )
}

function sliceHasCitations(slice: Slice): boolean {
  let found = false
  slice.content.descendants((node) => {
    if (found) return false
    if (node.type.name === 'citation') {
      found = true
      return false
    }
    return true
  })
  return found
}

// ── Extension ────────────────────────────────────────────────────────────────

export const AuditProtectionExtension = Extension.create<unknown, AuditProtectionStorage>({
  name: 'auditProtection',

  addStorage() {
    return { hasShownBlockedToast: false }
  },

  addProseMirrorPlugins() {
    const storage = this.storage
    return [
      new Plugin({
        key: auditProtectionPluginKey,

        filterTransaction(tr, state) {
          if (!tr.docChanged) return true
          const meta = tr.getMeta(auditProtectionPluginKey) as { bypass?: boolean } | undefined
          if (meta?.bypass) return true

          const oldIds = collectCitationIds(state.doc)
          if (oldIds.size === 0) return true

          const newIds = collectCitationIds(tr.doc)
          for (const id of oldIds) {
            if (!newIds.has(id)) {
              if (!storage.hasShownBlockedToast) {
                storage.hasShownBlockedToast = true
                toast.info('Citations can be modified from the Review Panel.')
              }
              return false
            }
          }
          return true
        },

        props: {
          transformPasted(slice, view) {
            if (collectCitationIds(view.state.doc).size === 0) return slice
            if (!sliceHasCitations(slice)) return slice
            return stripCitationsFromSlice(slice)
          },
        },
      }),
    ]
  },
})
